import { useContext } from "react";
import { useLocation, type To } from "react-router-dom";
import { searchWithParams } from "@components/auditLog/chat/TaskSummaryLinks";
import { getActionsTasksAccountsNavPaths } from "@components/navigation/getActionsTasksAccountsNavPaths";
import { getOrganizationTaskLink } from "@components/organizationView/clients-tasks/organizationTaskLink";
import { getOpenTaskLocationState } from "@components/tasks/useOpenTaskUrl";
import { ScopeContext, useScope } from "@state/scopeContext.lib";

export interface EntityRefLink {
    to: To;
    replace?: boolean;
    state?: ReturnType<typeof getOpenTaskLocationState>;
}

/**
 * Where a task pill leads. The organization view opens the task on the
 * organization Tasks page; a workspace opens it on its Tasks page, swapping
 * in place when a task is already open there so Back still returns to the
 * list. `null` until the pill's workspace is known.
 */
export function useTaskRefLink(taskId: string): EntityRefLink | null {
    const { workspaceId } = useScope();
    const { workspaceId: activeWorkspaceId, organizationId } =
        useContext(ScopeContext);
    const { pathname, search, state } = useLocation();

    if (!workspaceId) {
        return null;
    }

    const isOrganizationView = !activeWorkspaceId && !!organizationId;
    if (isOrganizationView) {
        return { to: getOrganizationTaskLink({ taskId, workspaceId }) };
    }

    const { tasksTabPath } = getActionsTasksAccountsNavPaths();
    if (pathname !== tasksTabPath) {
        return { to: { pathname: tasksTabPath, search: `?taskId=${taskId}` } };
    }

    const isTaskOpen = new URLSearchParams(search).has("taskId");
    return {
        to: {
            pathname: tasksTabPath,
            search: searchWithParams({ search, params: `taskId=${taskId}` }),
        },
        replace: isTaskOpen,
        state: isTaskOpen ? getOpenTaskLocationState(state) : undefined,
    };
}
